/** @jsxImportSource theme-ui */
import { Editor, EditorState, getDefaultKeyBinding, RichUtils } from 'draft-js';
import { useField } from 'formik';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import './Draftjs-css/draft-styles.css';
import 'draft-js/dist/Draft.css';

import { draftjs_EditorStateFromHtml, draftjsMapEditorStateBackToHtml } from './draftjsHelpers';
import { SemanticFormikProps } from './FormikSemanticProps';

export const FormikRichtextInputDraftjs: React.FC<SemanticFormikProps> = ({
  name,
  placeholder,
  error,
}) => {
  const [field, , helpers] = useField(name);
  const { setValue: setFormikValue, setTouched } = helpers;
  const { value } = field;

  // NOTE: much of this code is taken from Draftjs example
  // LINK: https://github.com/facebook/draft-js/blob/9b9376bd041559766fe86786c749a0d033c837b1/website/src/components/DraftEditorExample/index.js

  const initialStateMemo = useMemo(
    () => (value ? draftjs_EditorStateFromHtml(value) : EditorState.createEmpty()),
    []
  );
  const [editorState, setEditorState] = useState(initialStateMemo);
  const editor = useRef<Editor>(null);

  const focus = () => {
    if (editor.current) editor.current.focus();
  };

  const handleEditorOnChange = useCallback((newState: EditorState) => {
    setEditorState(newState);
    const markup = draftjsMapEditorStateBackToHtml(newState);
    setFormikValue(markup);
  }, []);

  const handleKeyCommand = useCallback(
    (command, editorState) => {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        handleEditorOnChange(newState);
        return 'handled';
      }
      return 'not-handled';
    },
    [editorState, handleEditorOnChange]
  );

  const mapKeyToEditorCommand = useCallback(
    e => {
      switch (e.keyCode) {
        case 9: // TAB
          // eslint-disable-next-line no-case-declarations
          const newEditorState = RichUtils.onTab(e, editorState, 4); // maxDepth=4
          if (newEditorState !== editorState) {
            handleEditorOnChange(newEditorState);
          }
          return null;
      }
      return getDefaultKeyBinding(e);
    },
    [editorState, handleEditorOnChange]
  );

  // If the user changes block type before entering any text, we can
  // either style the placeholder or hide it. Let's just hide it now.
  let className = 'RichEditor-editor';
  const contentState = editorState.getCurrentContent();
  if (!contentState.hasText()) {
    if (contentState.getBlockMap().first().getType() !== 'unstyled') {
      className += ' RichEditor-hidePlaceholder';
    }
  }

  return (
    <div className="RichEditor-root">
      <BlockStyleControls
        editorState={editorState}
        onToggle={blockType => {
          const newState = RichUtils.toggleBlockType(editorState, blockType);
          handleEditorOnChange(newState);
        }}
      />
      <InlineStyleControls
        editorState={editorState}
        onToggle={inlineStyle => {
          const newState = RichUtils.toggleInlineStyle(editorState, inlineStyle);
          handleEditorOnChange(newState);
        }}
      />
      <div className={className} onClick={focus}>
        <Editor
          blockStyleFn={getBlockStyle}
          customStyleMap={styleMap}
          editorState={editorState}
          handleKeyCommand={handleKeyCommand}
          keyBindingFn={mapKeyToEditorCommand}
          onChange={handleEditorOnChange}
          placeholder={placeholder}
          ref={editor}
          spellCheck
          onBlur={() => setTouched(true)}
        />
      </div>
    </div>
  );
};

// =====================================================================================================================================

// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
};

function getBlockStyle(block) {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    default:
      return null;
  }
}

function StyleButton({ onToggle, active, label, style }) {
  let className = 'RichEditor-styleButton';
  if (active) {
    className += ' RichEditor-activeButton';
  }

  return (
    <span
      className={className}
      onMouseDown={e => {
        e.preventDefault();
        onToggle(style);
      }}
    >
      {label}
    </span>
  );
}

const BLOCK_TYPES = [
  { label: 'H1', style: 'header-one' },
  { label: 'H2', style: 'header-two' },
  { label: 'H3', style: 'header-three' },
  { label: 'H4', style: 'header-four' },
  { label: 'H5', style: 'header-five' },
  { label: 'H6', style: 'header-six' },
  { label: 'Blockquote', style: 'blockquote' },
  { label: 'UL', style: 'unordered-list-item' },
  { label: 'OL', style: 'ordered-list-item' },
];

function BlockStyleControls({ editorState, onToggle }) {
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();

  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map(type => (
        <StyleButton
          key={type.label}
          active={type.style === blockType}
          label={type.label}
          onToggle={onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
}

const INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD' },
  { label: 'Italic', style: 'ITALIC' },
  { label: 'Underline', style: 'UNDERLINE' },
  { label: 'Monospace', style: 'CODE' },
];

function InlineStyleControls({ editorState, onToggle }) {
  const currentStyle = editorState.getCurrentInlineStyle();
  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map(type => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
}
